import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  AccountBalance,
  ListAlt,
  TipsAndUpdates,
  TrendingUp,
  CheckCircle,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';

interface WelcomeDialogProps {
  forceOpen?: boolean;
  onForceClose?: () => void;
}

const WelcomeDialog: React.FC<WelcomeDialogProps> = ({ forceOpen = false, onForceClose }) => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      return;
    }

    // Wait for user authentication to complete
    const user = auth.currentUser;
    if (!user) {
      // Wait a bit for auth to complete
      const timer = setTimeout(() => {
        const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
        if (!hasSeenWelcome && auth.currentUser) {
          console.log('📋 Showing Welcome Dialog for new user');
          setOpen(true);
        }
      }, 1000); // 1 second delay to ensure auth is complete

      return () => clearTimeout(timer);
    } else {
      // User is already authenticated, check immediately
      const hasSeenWelcome = localStorage.getItem('hasSeenWelcome');
      if (!hasSeenWelcome) {
        console.log('📋 Showing Welcome Dialog for authenticated user');
        setOpen(true);
      }
    }
  }, [forceOpen]);

  const handleClose = () => {
    if (!forceOpen) {
      localStorage.setItem('hasSeenWelcome', 'true');
    }
    setOpen(false);
    if (onForceClose) {
      onForceClose();
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleGetStarted = () => {
    handleClose();
    navigate('/portfolio/main');
  };

  const steps = [
    {
      label: 'Welcome to Trade Advisor',
      content: (
        <Box>
          <Typography variant="h5" gutterBottom>
            Your Smart Swing Trading Assistant
          </Typography>
          <Typography paragraph>
            Trade Advisor helps you make informed trading decisions by analyzing market data 
            and providing actionable recommendations.
          </Typography>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <TrendingUp color="primary" sx={{ mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Intelligent Analysis
                  </Typography>
                  <Typography variant="body2">
                    Our algorithms analyze technical indicators to identify optimal entry and exit points.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined">
                <CardContent>
                  <AccountBalance color="primary" sx={{ mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Portfolio Management
                  </Typography>
                  <Typography variant="body2">
                    Track multiple portfolios, both real and virtual, to test strategies risk-free.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ),
    },
    {
      label: 'Getting Started',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Here's how to get started with Trade Advisor:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Create a Portfolio"
                secondary="Set up your first portfolio with your investment amount and position limits"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Build Your Watchlist"
                secondary="Add stocks you want to monitor or import from MarketSurge"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Configure Trading Rules"
                secondary="Set your risk tolerance and trading preferences"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Review Recommendations"
                secondary="Get daily buy/sell recommendations based on your rules"
              />
            </ListItem>
          </List>
        </Box>
      ),
    },
    {
      label: 'Key Features',
      content: (
        <Box>
          <Typography variant="h6" gutterBottom>
            Powerful Features at Your Fingertips
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <ListAlt color="primary" sx={{ mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Smart Watchlist
                  </Typography>
                  <Typography variant="body2">
                    Track stocks with pivot prices, volume indicators, and technical analysis data.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <TipsAndUpdates color="primary" sx={{ mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    AI-Powered Recommendations
                  </Typography>
                  <Typography variant="body2">
                    Get entry and exit recommendations based on technical indicators and your rules.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <TrendingUp color="primary" sx={{ mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Autopilot Mode
                  </Typography>
                  <Typography variant="body2">
                    Test strategies automatically with virtual portfolios in autopilot mode.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5">Welcome to Trade Advisor!</Typography>
          <Button onClick={handleClose} color="inherit">
            Skip
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                <Box sx={{ mb: 2 }}>
                  {step.content}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    onClick={index === steps.length - 1 ? handleGetStarted : handleNext}
                    sx={{ mt: 1, mr: 1 }}
                    endIcon={index === steps.length - 1 ? <ArrowForward /> : null}
                  >
                    {index === steps.length - 1 ? 'Get Started' : 'Continue'}
                  </Button>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          <Button onClick={handleClose} color="inherit">
            Close
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              handleClose();
              navigate('/help');
            }}
          >
            View Help Center
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeDialog;